import React, {memo, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    Autocomplete,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    TextField,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import Swal from 'sweetalert2';
import {SNACKBAR_OPEN} from 'store/actions';
import {makeProduct} from 'types/product';
import IItem from 'types/product_delivery';
import JSDB from '../../../fromKotlin/nk';
import {dateObjectToString, roundDecimals} from '../../../fromKotlin/nkutils';
import {itemApiIInternal} from '../../../api/customer/products_prices.api';
import {
    addSupplierProduct,
    getSupplierProduct,
    selectSuppliersProductsDistinctCategoryValues,
    selectSuppliersProductsUnits,
} from 'store/SupplierProducts/supplierProductsSlice';

export interface FastAddSupplierProductDialogProps {
    open: boolean;
    supplierId: string;
    documentDate?: Date;
    onClose: () => void;
    onCreated: (item: IItem) => void;
}

const unitName = (product: any): string => {
    try {
        const name = product?.getEffectiveUnitName?.() ?? '';
        return name || 'יח\'';
    } catch {
        return 'יח\'';
    }
};

const lineFromProduct = (product: any, supplier: any, price: number): IItem => {
    const unit = unitName(product);
    let extra = '';
    try {
        extra = product?.getEffectiveExtraUnitName?.() ?? '';
    } catch {
        extra = '';
    }
    let rate = 0;
    try {
        rate = Number(product?.effectiveConversionRate?.() ?? 0) || 0;
    } catch {
        rate = 0;
    }
    const units = extra ? [unit, extra] : [unit];
    let barcode = '';
    try {
        const raw = product?.getBarcode?.() ?? '';
        barcode = raw && raw !== '0000000000000' ? raw : '';
    } catch {
        barcode = '';
    }
    return {
        quantity: 1,
        quantityReturns: 0,
        quantitySecond: 0,
        quantitySecondReturns: 0,
        id: Number(product.id),
        name: product.getName?.() ?? '',
        barcode,
        position: Number(product.getPosition?.() ?? 0),
        no_tax_product: product.getNoTaxProduct?.() === 1,
        unit,
        available_units: units,
        hasSecondUnitName: !!extra,
        image: '',
        category: product.getCategory?.() ?? '',
        category2: product.getCategory2?.() ?? '',
        conversion_ratio: rate,
        totalPrice: price,
        discount: 0,
        nkObject: product,
        ent: supplier,
        priceTouched: price > 0,
    };
};

const FastAddSupplierProductDialog = ({
    open,
    supplierId,
    documentDate,
    onClose,
    onCreated,
}: FastAddSupplierProductDialogProps) => {
    const dispatch = useDispatch();
    const categories: string[] = useSelector((state: any) => selectSuppliersProductsDistinctCategoryValues(state)) ?? [];
    const units: string[] = useSelector((state: any) => selectSuppliersProductsUnits(state)) ?? [];
    const [name, setName] = useState('');
    const [barcode, setBarcode] = useState('');
    const [category, setCategory] = useState('');
    const [unit, setUnit] = useState('');
    const [price, setPrice] = useState('');
    const [noTax, setNoTax] = useState(false);
    const [nameError, setNameError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName('');
        setBarcode('');
        setCategory('');
        setUnit('');
        setPrice('');
        setNoTax(false);
        setNameError('');
        setSaving(false);
    }, [open]);

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setNameError('שם המוצר הינו שדה חובה');
            return;
        }
        const parsedPrice = Number(price);
        const linePrice = price.trim() === '' || !Number.isFinite(parsedPrice)
            ? 0
            : roundDecimals(Math.max(0, parsedPrice), 4);
        setSaving(true);
        try {
            const created = makeProduct({
                name: trimmed,
                barcode: barcode.trim(),
                category: category.trim(),
                unit: unit.trim(),
                default_price: linePrice,
                isNoTax: noTax,
                connected_products: [],
            });
            const result: any = await dispatch(addSupplierProduct(created as any)).unwrap();
            if (!result || result.second?.name !== 'SUCCESS' || result.first == null) {
                throw new Error('create failed');
            }
            const productId = Number(result.first.id);
            try {
                await dispatch(getSupplierProduct(true) as any).unwrap();
            } catch {
                // The product is already in the local catalog. The line can still be added.
            }
            const supplier = JSDB().getSupplier(Number(supplierId))?.first;
            const kotlinProduct = JSDB().getSupplierProduct(productId)?.first ?? result.first;
            const date = dateObjectToString(documentDate ?? new Date());
            let line: IItem;
            const priced = supplier?.getPrice?.(productId);
            if (supplier && priced) {
                line = itemApiIInternal(supplier, date, productId, true);
                line = {
                    ...line,
                    quantity: 1,
                    totalPrice: linePrice > 0 ? linePrice : line.totalPrice,
                    priceTouched: linePrice > 0 || line.priceTouched,
                };
            } else {
                line = lineFromProduct(kotlinProduct, supplier, linePrice);
            }
            line = {...line, no_tax_product: noTax};
            onCreated(line);
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                message: 'מוצר הספק נוסף לתעודה',
                variant: 'alert',
                alertSeverity: 'success',
            });
        } catch {
            Swal.fire({
                title: 'שגיאה בהוספת מוצר',
                icon: 'error',
                confirmButtonText: 'המשך',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>מוצר ספק חדש</DialogTitle>
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    if (!saving) handleSave();
                }}
            >
            <DialogContent>
                <Grid container spacing={2} sx={{mt: 0.5}}>
                    <Grid item xs={12}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="שם המוצר"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                if (nameError) setNameError('');
                            }}
                            error={Boolean(nameError)}
                            helperText={nameError}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="ברקוד"
                            value={barcode}
                            onChange={(e) => setBarcode(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            freeSolo
                            options={categories}
                            inputValue={category}
                            onInputChange={(event, value) => {
                                if (event) setCategory(value ?? '');
                            }}
                            renderInput={(params) => <TextField {...params} label="קטגוריה" />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            freeSolo
                            options={units}
                            inputValue={unit}
                            onInputChange={(event, value) => {
                                if (event) setUnit(value ?? '');
                            }}
                            renderInput={(params) => <TextField {...params} label="יחידה" />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="מחיר"
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            inputProps={{min: 0, step: '0.01'}}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={noTax}
                                    onChange={(event) => setNoTax(event.target.checked)}
                                    color="primary"
                                />
                            }
                            label="ללא מעמ"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>ביטול</Button>
                <LoadingButton loading={saving} variant="contained" type="submit">
                    צור והוסף לתעודה
                </LoadingButton>
            </DialogActions>
            </form>
        </Dialog>
    );
};

export default memo(FastAddSupplierProductDialog);
